import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { playSoundEffect } from '../../../hooks/useSoundEffects';
import { getExploreUnknownMinigameWinRewardImageSrc } from '../../../config/exploreUnknownMinigameWinRewardImages';
import {
  REWARD_MEMORY_BACKGROUND_SRC,
  REWARD_MEMORY_CARD_BACK_SRC,
  REWARD_MEMORY_FLIP_SOUND_SRC,
  REWARD_MEMORY_MATCH_SOUND_SRC,
  REWARD_MEMORY_WIN_PICK_VALUE,
  REWARD_MEMORY_LOSS_PICK_VALUE,
  REWARD_MEMORY_MISMATCH_HIDE_MS,
  REWARD_MEMORY_GRID_GAP_PX,
  computeRewardMemoryCardSizePx,
  REWARD_MEMORY_CARD_ART_INSET_RATIO,
  REWARD_MEMORY_CARD_FACE_BACKGROUND,
} from './rewardMemoryLayout';
import './RewardMemoryMinigame.css';

const START_HEALTH_PLUS3_REWARD_ID = 'startHealthPlus3';

/**
 * @param {object} props
 * @param {{ maxAttempts: number, maxRewardsPerGame: number, cardRewardIds: string[], winPickValue: number, lossPickValue: number }} props.challenge
 * @param {(pickedTreeIndex: number, opts?: { matchedPairs?: { firstCardIndex: number, secondCardIndex: number }[] }) => void} props.onPickTree
 * @param {boolean} props.disabled
 * @param {React.RefObject<HTMLElement>} props.containerRef
 * @param {null | 'won' | 'lost'} props.resultOutcome
 * @param {(() => void) | undefined} props.onResultProceed
 * @param {{ id: string } | null} props.winReward
 * @param {Array<{ id: string }> | null} props.winRewards
 */
export default function RewardMemoryMinigame({
  challenge,
  onPickTree,
  disabled = false,
  containerRef,
  resultOutcome = null,
  onResultProceed,
  winReward = null,
  winRewards = null,
}) {
  const { t } = useTranslation();
  const playArenaRef = useRef(null);
  const submittedRef = useRef(false);
  const mismatchTimerRef = useRef(null);

  const maxAttempts = challenge.maxAttempts;
  const maxRewardsPerGame = challenge.maxRewardsPerGame;
  const cardRewardIds = challenge.cardRewardIds;
  const winPickValue = challenge.winPickValue ?? REWARD_MEMORY_WIN_PICK_VALUE;
  const lossPickValue = challenge.lossPickValue ?? REWARD_MEMORY_LOSS_PICK_VALUE;

  const [attemptsRemaining, setAttemptsRemaining] = useState(maxAttempts);
  const [rewardsCollected, setRewardsCollected] = useState(0);
  const [flippedIndices, setFlippedIndices] = useState([]);
  const [matchedIndices, setMatchedIndices] = useState([]);
  const [matchedPairs, setMatchedPairs] = useState([]);
  const [resolvingPair, setResolvingPair] = useState(false);
  const [cardSizePx, setCardSizePx] = useState(null);

  const resolvedWinRewards =
    Array.isArray(winRewards) && winRewards.length > 0
      ? winRewards
      : winReward && typeof winReward.id === 'string'
        ? [winReward]
        : [];

  const setPlayArenaRef = useCallback(
    (node) => {
      playArenaRef.current = node;
      if (containerRef && typeof containerRef === 'object') {
        containerRef.current = node;
      }
    },
    [containerRef]
  );

  useEffect(() => {
    return () => {
      if (mismatchTimerRef.current != null) {
        window.clearTimeout(mismatchTimerRef.current);
        mismatchTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const arena = playArenaRef.current;
    if (!arena || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const updateCardSize = () => {
      const rect = arena.getBoundingClientRect();
      setCardSizePx(computeRewardMemoryCardSizePx(rect.width, rect.height));
    };
    updateCardSize();
    const observer = new ResizeObserver(() => updateCardSize());
    observer.observe(arena);
    return () => observer.disconnect();
  }, []);

  const gridLayoutStyle =
    cardSizePx != null
      ? {
          '--reward-memory-card-size': `${cardSizePx}px`,
          '--reward-memory-grid-gap': `${REWARD_MEMORY_GRID_GAP_PX}px`,
        }
      : undefined;

  const cardInsetPct = `${REWARD_MEMORY_CARD_ART_INSET_RATIO * 100}%`;
  const cardArtSpanPct = `${(1 - 2 * REWARD_MEMORY_CARD_ART_INSET_RATIO) * 100}%`;
  const cardFacePanelStyle =
    cardSizePx != null
      ? {
          left: cardInsetPct,
          top: cardInsetPct,
          width: cardArtSpanPct,
          height: cardArtSpanPct,
          backgroundColor: REWARD_MEMORY_CARD_FACE_BACKGROUND,
        }
      : undefined;

  const submitSessionEnd = useCallback(
    (pairsCollected) => {
      if (submittedRef.current || disabled || resultOutcome != null) {
        return;
      }
      submittedRef.current = true;
      if (pairsCollected.length > 0) {
        onPickTree(winPickValue, { matchedPairs: pairsCollected });
      } else {
        onPickTree(lossPickValue);
      }
    },
    [disabled, lossPickValue, onPickTree, resultOutcome, winPickValue]
  );

  const scheduleSessionEndIfDone = useCallback(
    (nextAttempts, nextRewards, pairsSnapshot) => {
      if (nextRewards >= maxRewardsPerGame || nextAttempts <= 0) {
        window.setTimeout(() => submitSessionEnd(pairsSnapshot), 400);
      }
    },
    [maxRewardsPerGame, submitSessionEnd]
  );

  const resolvePair = useCallback(
    (firstIndex, secondIndex) => {
      const firstReward = cardRewardIds[firstIndex];
      const secondReward = cardRewardIds[secondIndex];

      if (firstReward !== secondReward) {
        mismatchTimerRef.current = window.setTimeout(() => {
          mismatchTimerRef.current = null;
          setFlippedIndices([]);
          setResolvingPair(false);
          setAttemptsRemaining((prevAttempts) => {
            const nextAttempts = prevAttempts - 1;
            setRewardsCollected((prevRewards) => {
              setMatchedPairs((pairs) => {
                scheduleSessionEndIfDone(nextAttempts, prevRewards, pairs);
                return pairs;
              });
              return prevRewards;
            });
            return nextAttempts;
          });
        }, REWARD_MEMORY_MISMATCH_HIDE_MS);
        return;
      }

      playSoundEffect(REWARD_MEMORY_MATCH_SOUND_SRC);
      const nextPair = { firstCardIndex: firstIndex, secondCardIndex: secondIndex };
      setMatchedPairs((prevPairs) => {
        const nextPairs = [...prevPairs, nextPair];
        setRewardsCollected((prevRewards) => {
          const nextRewards = prevRewards + 1;
          setMatchedIndices((prev) => [...prev, firstIndex, secondIndex]);
          setFlippedIndices([]);
          setResolvingPair(false);
          setAttemptsRemaining((prevAttempts) => {
            const nextAttempts = prevAttempts - 1;
            scheduleSessionEndIfDone(nextAttempts, nextRewards, nextPairs);
            return nextAttempts;
          });
          return nextRewards;
        });
        return nextPairs;
      });
    },
    [cardRewardIds, scheduleSessionEndIfDone]
  );

  const handleCardActivate = useCallback(
    (cardIndex) => {
      if (
        submittedRef.current ||
        disabled ||
        resultOutcome != null ||
        resolvingPair ||
        attemptsRemaining < 1 ||
        rewardsCollected >= maxRewardsPerGame
      ) {
        return;
      }
      if (matchedIndices.includes(cardIndex) || flippedIndices.includes(cardIndex)) {
        return;
      }
      if (flippedIndices.length >= 2) {
        return;
      }
      playSoundEffect(REWARD_MEMORY_FLIP_SOUND_SRC);
      const nextFlipped = [...flippedIndices, cardIndex];
      setFlippedIndices(nextFlipped);
      if (nextFlipped.length < 2) {
        return;
      }
      setResolvingPair(true);
      resolvePair(nextFlipped[0], nextFlipped[1]);
    },
    [
      attemptsRemaining,
      disabled,
      flippedIndices,
      matchedIndices,
      maxRewardsPerGame,
      resolvePair,
      resolvingPair,
      resultOutcome,
      rewardsCollected,
    ]
  );

  const isCardFaceUp = useCallback(
    (cardIndex) => matchedIndices.includes(cardIndex) || flippedIndices.includes(cardIndex),
    [flippedIndices, matchedIndices]
  );

  return (
    <div className="reward-memory">
      <div
        ref={setPlayArenaRef}
        className="reward-memory-play-arena"
        role="application"
        aria-label={t('exploreUnknownPage.minigame.rewardMemory.playArenaAria')}
      >
        <img
          className="reward-memory-bg"
          src={REWARD_MEMORY_BACKGROUND_SRC}
          alt=""
          draggable={false}
        />
        <div className="reward-memory-hud reward-memory-hud--top" data-no-minigame-flashlight="true">
          <h3 className="reward-memory-game-title">{t('exploreUnknownPage.minigame.rewardMemory.title')}</h3>
          <p className="reward-memory-explanation">{t('exploreUnknownPage.minigame.rewardMemory.explanation')}</p>
          {resultOutcome == null ? (
            <>
              <p className="reward-memory-attempts">
                {t('exploreUnknownPage.minigame.rewardMemory.attemptsRemaining', {
                  count: attemptsRemaining,
                })}
              </p>
              {rewardsCollected > 0 ? (
                <p className="reward-memory-rewards-kept">
                  {t('exploreUnknownPage.minigame.rewardMemory.rewardsKept', {
                    count: rewardsCollected,
                    max: maxRewardsPerGame,
                  })}
                </p>
              ) : null}
            </>
          ) : null}
        </div>
        {cardSizePx != null ? (
        <div
          className="reward-memory-grid"
          style={gridLayoutStyle}
          role="group"
          aria-label={t('exploreUnknownPage.minigame.rewardMemory.gridAria')}
        >
          {cardRewardIds.map((rewardId, cardIndex) => {
            const faceUp = isCardFaceUp(cardIndex);
            const rewardImageSrc = getExploreUnknownMinigameWinRewardImageSrc(rewardId);
            const labelKey = `exploreUnknownPage.minigame.rewardMemory.cardLabel.${rewardId}`;
            return (
              <button
                key={`memory-card-${cardIndex}`}
                type="button"
                className={`reward-memory-card${faceUp ? ' reward-memory-card--face-up' : ''}`}
                disabled={
                  disabled ||
                  resultOutcome != null ||
                  resolvingPair ||
                  attemptsRemaining < 1 ||
                  matchedIndices.includes(cardIndex) ||
                  (rewardsCollected >= maxRewardsPerGame && !matchedIndices.includes(cardIndex))
                }
                onClick={() => handleCardActivate(cardIndex)}
                aria-label={t('exploreUnknownPage.minigame.rewardMemory.cardAria', {
                  index: cardIndex + 1,
                })}
              >
                <span
                  className={`reward-memory-card-flipper${faceUp ? ' reward-memory-card-flipper--face-up' : ''}`}
                >
                  <span className="reward-memory-card-side reward-memory-card-side--back">
                    <img
                      className="reward-memory-card-layer reward-memory-card-back"
                      src={REWARD_MEMORY_CARD_BACK_SRC}
                      alt=""
                      draggable={false}
                    />
                  </span>
                  <span
                    className="reward-memory-card-side reward-memory-card-side--front"
                    aria-hidden={!faceUp}
                  >
                    <span className="reward-memory-card-face-panel" style={cardFacePanelStyle}>
                      <span className="reward-memory-card-reward-wrap">
                        <img
                          className="reward-memory-card-reward"
                          src={rewardImageSrc}
                          alt={t(labelKey)}
                          draggable={false}
                        />
                        {rewardId === START_HEALTH_PLUS3_REWARD_ID ? (
                          <span
                            className="reward-memory-start-health-plus-badge"
                            aria-hidden="true"
                          >
                            <span className="reward-memory-start-health-plus-char">+</span>
                            <span className="reward-memory-start-health-plus-char">+</span>
                            <span className="reward-memory-start-health-plus-char">+</span>
                          </span>
                        ) : null}
                      </span>
                    </span>
                  </span>
                </span>
              </button>
            );
          })}
        </div>
        ) : null}
        {resultOutcome != null ? (
          <div
            className={`reward-memory-hud reward-memory-hud--bottom reward-memory-result-banner reward-memory-result-banner--${resultOutcome}`}
            role="status"
            aria-live="polite"
            data-no-minigame-flashlight="true"
          >
            <p className="reward-memory-result-banner-title">
              {resultOutcome === 'won'
                ? t('exploreUnknownPage.minigame.rewardMemory.successTitle')
                : t('exploreUnknownPage.minigame.rewardMemory.failureTitle')}
            </p>
            {resultOutcome === 'won' ? (
              <div className="reward-memory-result-rewards">
                {resolvedWinRewards.map((reward) => {
                  const bodyKey = `exploreUnknownPage.minigame.rewardMemory.winReward.${reward.id}`;
                  return (
                    <p key={reward.id} className="reward-memory-result-banner-body">
                      {t(bodyKey, {
                        percent: reward.darknessReductionApplied,
                        xp: reward.xpGained,
                        billyBucks: reward.billyBucksAmount,
                        health: reward.startHealthBonus != null ? reward.startHealthBonus : 3,
                      })}
                    </p>
                  );
                })}
              </div>
            ) : (
              <p className="reward-memory-result-banner-body">
                {t('exploreUnknownPage.minigame.rewardMemory.failureBody')}
              </p>
            )}
            {typeof onResultProceed === 'function' ? (
              <button
                type="button"
                className={
                  resultOutcome === 'lost'
                    ? 'reward-memory-result-proceed reward-memory-result-proceed--lost'
                    : 'reward-memory-result-proceed'
                }
                onClick={() => onResultProceed()}
              >
                {t('exploreUnknownPage.minigame.proceed')}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
